import { KitButton } from './button';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { sanitizeRichTextDocument } from '../tier0/kit-richtext.mjs';
import type { FieldControlProps } from './form-field';
import {
  insertPlainRichText,
  readRichTextArea,
  restoreRichTextSelection,
  richTextOffsets,
  richTextSelection,
  safeRichTextLink,
  writeRichTextArea,
} from './rich-text-dom';
import { transformRichTextBlock, transformRichTextMark } from './rich-text-edit';

type Command = 'bold' | 'italic' | 'heading' | 'list' | 'numbered' | 'link' | 'code';
const commands: Command[] = ['bold', 'italic', 'heading', 'list', 'numbered', 'link', 'code'];
const activeTag: Record<string, Command> = {
  STRONG: 'bold',
  B: 'bold',
  EM: 'italic',
  I: 'italic',
  H2: 'heading',
  UL: 'list',
  OL: 'numbered',
  A: 'link',
  CODE: 'code',
};
export function RichTextEditor({
  label,
  copy,
  requestLink,
  onError,
  readOnly = false,
  ...props
}: FieldControlProps & {
  label: string;
  copy: Record<Command, string>;
  readOnly?: boolean;
  requestLink(): Promise<string | null>;
  onError(error: unknown): void;
}) {
  const area = useRef<HTMLDivElement>(null);
  const last = useRef('');
  const revision = useRef(0);
  const [pressed, setPressed] = useState<Set<Command>>(new Set());
  const doc = sanitizeRichTextDocument(props.value ?? { type: 'doc', content: [] });
  const signature = JSON.stringify(doc);
  const locked = props.disabled || readOnly;
  const currentLocked = useRef(locked);
  currentLocked.current = locked;
  useLayoutEffect(() => {
    if (area.current && last.current !== signature) {
      writeRichTextArea(area.current, doc);
      last.current = signature;
      revision.current++;
    }
  }, [signature]);
  useEffect(() => {
    const root = area.current;
    const update = () => {
      const range = root && richTextSelection(root);
      let node =
        range?.startContainer.nodeType === 1
          ? (range.startContainer as Element)
          : range?.startContainer.parentElement;
      const result = new Set<Command>();
      while (node && node !== root) {
        if (activeTag[node.tagName]) result.add(activeTag[node.tagName]);
        node = node.parentElement;
      }
      setPressed(result);
    };
    root?.ownerDocument.addEventListener('selectionchange', update);
    return () => {
      root?.ownerDocument.removeEventListener('selectionchange', update);
      revision.current++;
    };
  }, []);
  const report = () => {
    if (locked || !area.current) return;
    try {
      const next = readRichTextArea(area.current);
      last.current = JSON.stringify(next);
      revision.current++;
      props.onValueChange(next);
    } catch (error) {
      onError(error);
    }
  };
  const command = async (kind: Command) => {
    const root = area.current;
    const range = root && richTextSelection(root);
    if (locked || !root || !range) return;
    const { start, end } = richTextOffsets(root, range);
    const ticket = revision.current;
    try {
      const current = readRichTextArea(root);
      let next;
      if (kind === 'heading' || kind === 'list' || kind === 'numbered') {
        let node: Node | null = range.startContainer;
        while (node && node.parentNode !== root) node = node.parentNode;
        const index = node ? Array.from(root.childNodes).indexOf(node as ChildNode) : 0;
        next = transformRichTextBlock(current, index, kind);
      } else {
        const href = kind === 'link' ? await requestLink() : null;
        if (
          currentLocked.current ||
          revision.current !== ticket ||
          (kind === 'link' && href === null)
        )
          return;
        next = transformRichTextMark(
          current,
          start,
          end,
          kind === 'link' ? safeRichTextLink(href!) : { type: kind },
        );
      }
      if (currentLocked.current || revision.current !== ticket) return;
      writeRichTextArea(root, next);
      root.focus();
      restoreRichTextSelection(root, start, end);
      report();
    } catch (error) {
      onError(error);
    }
  };
  return (
    <div
      className="pc-rte"
      data-state={props.disabled ? 'disabled' : readOnly ? 'readonly' : undefined}
    >
      <div className="pc-rte-bar" role="toolbar" aria-label={label}>
        {commands.map((kind) => (
          <KitButton
            variant="quiet"
            key={kind}
            type="button"
            className="pc-tool"
            disabled={locked}
            aria-pressed={pressed.has(kind)}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => void command(kind)}
          >
            {copy[kind]}
          </KitButton>
        ))}
      </div>
      <div className="pc-field pc-field-md">
        <div
          id={props.id}
          ref={area}
          className="pc-rte-area"
          role="textbox"
          aria-label={label}
          aria-multiline="true"
          aria-readonly={readOnly}
          aria-disabled={props.disabled}
          aria-invalid={props['aria-invalid']}
          aria-describedby={props['aria-describedby']}
          contentEditable={!locked}
          suppressContentEditableWarning
          onInput={report}
          onBlur={props.onBlur}
          onPaste={(event) => {
            event.preventDefault();
            if (!locked && area.current) {
              insertPlainRichText(area.current, event.clipboardData.getData('text/plain'));
              report();
            }
          }}
          onDrop={(event) => {
            event.preventDefault();
            if (!locked && area.current) {
              insertPlainRichText(area.current, event.dataTransfer.getData('text/plain'));
              report();
            }
          }}
        />
      </div>
    </div>
  );
}
